import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencyDollarIcon as CurrencyDollar, TrendUpIcon as TrendUp, ReceiptIcon as Receipt, WalletIcon as Wallet, PlusIcon as Plus, TrashIcon as Trash, DownloadSimpleIcon as DownloadSimple, CalculatorIcon as Calculator } from "@phosphor-icons/react";

/**
 * `/admin/accounting` — lightweight P&L + manual expense entry.
 *
 * Founder-facing only. Renders a month selector, 4 KPI cards
 * (Gross / Fees / Refunds / Net), a SKU breakdown table, an expense
 * form, and the expense list for the same month. CSV export downloads
 * the same numbers for accountant hand-off.
 *
 * Numbers come from `/api/admin/accounting/pnl?month=YYYY-MM`. The
 * backend explicitly excludes escrow pass-through from gross revenue
 * and surfaces the platform's commission slice as its own SKU row.
 */
function currentMonthYM() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

function fmtUSD(n) {
  return `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function KpiCard({ label, value, icon: Icon, tone = "ink" }) {
  const toneStyles = {
    ink: "border-[#E8E0CC] bg-white",
    gold: "border-gold/40 bg-gradient-to-br from-[#FBF7EC] to-white",
    crimson: "border-crimson/30 bg-[#FEF5F5]",
  };
  return (
    <div
      className={`rounded-2xl border p-5 ${toneStyles[tone]}`}
      data-testid={`accounting-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} weight="duotone" className={tone === "gold" ? "text-gold-dark" : tone === "crimson" ? "text-crimson" : "text-ink"} />
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6B6B] font-bold">
          {label}
        </span>
      </div>
      <div className="font-serif text-2xl text-ink">{fmtUSD(value)}</div>
    </div>
  );
}

export default function AdminAccounting() {
  const [month, setMonth] = useState(currentMonthYM());
  const [pnl, setPnl] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    category: "hosting",
    amount: "",
    vendor: "",
    incurred_on: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [pnlRes, expRes] = await Promise.all([
        api.get(`/admin/accounting/pnl?month=${month}`),
        api.get(`/admin/accounting/expenses?month=${month}`),
      ]);
      setPnl(pnlRes.data);
      setExpenses(expRes.data.expenses || []);
      setCategories(expRes.data.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load accounting data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  async function submitExpense(e) {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter an amount > 0");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/accounting/expenses", {
        category: form.category,
        amount: Number(form.amount),
        vendor: form.vendor,
        incurred_on: form.incurred_on,
        notes: form.notes,
      });
      toast.success("Expense recorded");
      setForm({
        category: "hosting",
        amount: "",
        vendor: "",
        incurred_on: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add expense");
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteExpense(id) {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/admin/accounting/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  }

  async function downloadCsv() {
    try {
      const res = await api.get(`/admin/accounting/pnl.csv?month=${month}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pnl-${month}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV export failed");
    }
  }

  async function openInSheets() {
    const { openInGoogleSheets } = await import("@/lib/google-sheets");
    await openInGoogleSheets({
      api,
      endpoint: `/admin/accounting/pnl.csv?month=${month}`,
      filenameHint: `pnl-${month}.csv`,
    }).catch(() => {});
  }

  // Build a list of months to choose from — the last 18 months.
  const monthOptions = useMemo(() => {
    const out = [];
    const d = new Date();
    d.setUTCDate(1);
    for (let i = 0; i < 18; i++) {
      const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      out.push(ym);
      d.setUTCMonth(d.getUTCMonth() - 1);
    }
    return out;
  }, []);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-7" data-testid="admin-accounting-page">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink flex items-center gap-3">
              <Calculator size={32} weight="duotone" className="text-gold-dark" />
              Accounting
            </h1>
            <p className="text-[#6B6B6B] mt-1 text-sm max-w-2xl">
              Monthly profit & loss across every paid service, with manual expense entry. Stripe fees are estimated at 2.9 % + $0.30/tx.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-[180px]" data-testid="accounting-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={downloadCsv}
              data-testid="accounting-export-csv"
            >
              <DownloadSimple size={16} className="mr-1.5" />
              CSV
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-emerald-600 text-emerald-700 hover:bg-emerald-50"
              onClick={openInSheets}
              data-testid="accounting-export-gsheets"
            >
              <DownloadSimple size={16} className="mr-1.5" />
              Sheets
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        {pnl && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Gross revenue" value={pnl.gross_revenue + pnl.escrow_commission} icon={TrendUp} tone="gold" />
            <KpiCard label="Stripe fees (est)" value={pnl.stripe_fees_est} icon={Receipt} />
            <KpiCard label="Refunds" value={pnl.refunds} icon={Wallet} tone={pnl.refunds > 0 ? "crimson" : "ink"} />
            <KpiCard label="Net profit" value={pnl.net_profit} icon={CurrencyDollar} tone={pnl.net_profit >= 0 ? "gold" : "crimson"} />
          </div>
        )}

        {/* Revenue by SKU table */}
        <section className="rounded-2xl border border-[#E8E0CC] bg-white">
          <header className="px-5 py-4 border-b border-[#E8E0CC]">
            <h2 className="font-serif text-xl text-ink">Revenue by SKU · {month}</h2>
            <p className="text-[12px] text-[#6B6B6B] mt-0.5">
              Escrow commission is the platform's slice of buyer-purchase volume — listed separately.
            </p>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="accounting-sku-table">
              <thead className="bg-[#FAF7EE] text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                <tr>
                  <th className="text-left px-5 py-2.5">SKU</th>
                  <th className="text-right px-5 py-2.5">Count</th>
                  <th className="text-right px-5 py-2.5">Gross</th>
                  <th className="text-right px-5 py-2.5">Fees (est)</th>
                  <th className="text-right px-5 py-2.5">Net</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F0E8D0]">
                {(pnl?.by_sku || []).map((r) => (
                  <tr key={r.kind} data-testid={`accounting-sku-${r.kind}`}>
                    <td className="px-5 py-2.5 text-ink">{r.kind.replace(/_/g, " ")}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{r.count}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{fmtUSD(r.gross)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-[#9C7E25]">-{fmtUSD(r.fees_est)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-semibold">{fmtUSD(r.net)}</td>
                  </tr>
                ))}
                {(pnl?.by_sku || []).length === 0 && !loading && (
                  <tr>
                    <td colSpan={5} className="px-5 py-6 text-center text-[#6B6B6B]">
                      No paid transactions this month.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Add expense + expense list */}
        <section className="grid lg:grid-cols-5 gap-5">
          <form
            onSubmit={submitExpense}
            className="lg:col-span-2 rounded-2xl border border-[#E8E0CC] bg-white p-5 space-y-3"
            data-testid="accounting-add-expense-form"
          >
            <h3 className="font-serif text-lg text-ink mb-1 flex items-center gap-2">
              <Plus size={18} weight="bold" className="text-gold-dark" />
              Add expense
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(categories.length ? categories : [
                      "hosting", "software", "marketing", "contractor", "legal", "insurance", "office", "travel", "fees", "other",
                    ]).map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Amount (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  data-testid="expense-amount-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Vendor</Label>
                <Input
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  placeholder="AWS, Figma, ..."
                  data-testid="expense-vendor-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Date</Label>
                <Input
                  type="date"
                  value={form.incurred_on}
                  onChange={(e) => setForm({ ...form, incurred_on: e.target.value })}
                  data-testid="expense-date-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional context — invoice number, period, etc."
                className="resize-none"
                rows={2}
                data-testid="expense-notes-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-full bg-ink text-white hover:bg-crimson"
              disabled={submitting}
              data-testid="expense-submit-btn"
            >
              {submitting ? "Saving…" : "Record expense"}
            </Button>
          </form>

          <div className="lg:col-span-3 rounded-2xl border border-[#E8E0CC] bg-white" data-testid="accounting-expense-list">
            <header className="px-5 py-4 border-b border-[#E8E0CC] flex items-center justify-between">
              <h3 className="font-serif text-lg text-ink">Expenses · {month}</h3>
              <span className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
                Total {fmtUSD(pnl?.expenses)}
              </span>
            </header>
            <div className="max-h-[480px] overflow-y-auto">
              {expenses.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-[#6B6B6B]">
                  No expenses recorded for this month yet.
                </div>
              ) : (
                <ul className="divide-y divide-[#F0E8D0]">
                  {expenses.map((e) => (
                    <li
                      key={e.id}
                      className="px-5 py-3 flex items-center justify-between gap-3"
                      data-testid={`expense-row-${e.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-ink truncate">
                          <span className="font-medium">{e.vendor || "—"}</span>
                          <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FBF7EC] text-gold-dark border border-gold/30 rounded-full px-2 py-0.5">
                            {e.category}
                          </span>
                        </div>
                        <div className="text-[11px] text-[#6B6B6B] mt-0.5">
                          {e.incurred_on} {e.notes ? `· ${e.notes}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-serif text-base text-ink tabular-nums">
                          {fmtUSD(e.amount)}
                        </span>
                        <button
                          type="button"
                          onClick={() => deleteExpense(e.id)}
                          className="text-[#9C7E25] hover:text-crimson p-1.5 rounded-md hover:bg-[#FEF5F5]"
                          aria-label="Delete expense"
                          data-testid={`expense-delete-${e.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>

        {pnl && (
          <section className="rounded-2xl border border-[#E8E0CC] bg-white p-5 text-sm text-[#3F3F3F]">
            <h3 className="font-serif text-base text-ink mb-2">Bottom line</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]">
              <div>Gross revenue: <span className="font-semibold tabular-nums">{fmtUSD(pnl.gross_revenue)}</span></div>
              <div>Escrow commission: <span className="font-semibold tabular-nums">{fmtUSD(pnl.escrow_commission)}</span></div>
              <div>Stripe fees: <span className="font-semibold text-[#9C7E25] tabular-nums">-{fmtUSD(pnl.stripe_fees_est)}</span></div>
              <div>Refunds: <span className="font-semibold text-crimson tabular-nums">-{fmtUSD(pnl.refunds)}</span></div>
              <div>Net revenue: <span className="font-semibold tabular-nums">{fmtUSD(pnl.net_revenue)}</span></div>
              <div>Expenses: <span className="font-semibold text-crimson tabular-nums">-{fmtUSD(pnl.expenses)}</span></div>
              <div className="col-span-2 md:col-span-2">
                Net profit:{" "}
                <span className={`font-serif text-xl tabular-nums ${pnl.net_profit >= 0 ? "text-ink" : "text-crimson"}`}>
                  {fmtUSD(pnl.net_profit)}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-[#6B6B6B] mt-3">
              Escrow GMV this month: {fmtUSD(pnl.escrow_volume)} (only the platform commission slice counts as revenue above).
            </p>
          </section>
        )}
      </div>
    </AppShell>
  );
}
